import { AfterViewInit, ChangeDetectorRef, Directive, inject, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { GuidEmpty, WizardComponent } from 'mbs-ui-kit';
import { AgentOptionsFacade } from 'mbs-ui-shared@facades/agent-options.facade';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { RemoteDeployDataFacade } from 'mbs-ui-shared@facades/remote-deploy-data.facade';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { StorageType } from 'mbs-ui-shared@models';
import { NotificationPerPlanSettings } from 'mbs-ui-shared@models/Notification';
import { PlanMode, PlanTypes, PlanTypesDisplay, PlanVMNewTypesDisplay } from 'mbs-ui-shared@models/PlanTypes.enum';
import { DefaultSelectData } from 'mbs-ui-shared@models/SelectData';
import { IncrementalData } from 'mbs-ui-shared@models/backup/schedule-models';
import { SimplePlanStatus } from 'mbs-ui-shared@models/backup/simple-plan-status';
import { RemoteDeployPlanModel } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-plan-model';
import { storageAccountsToDestinationsArray } from 'mbs-ui-shared@utils/storage-accounts-to-destinations-array';
import { combineLatest, first, Observable, ObservedValueOf, of, Subject, switchMap, take } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { StepsDataFromPlanHelper } from 'wizards/helpers/steps-data-from-plan-helper/steps-data-from-plan-helper';
import { Storages } from 'wizards/models/base/base-models/plan-storages-model';
import { PlansWizardMode } from 'wizards/models/base/base-plan-models';
import { SaveVariants } from 'wizards/models/base/other-base-models/save-variants';
import { PlanResponseModel } from 'wizards/models/base/plan-response-model';
import { CloudRestoreTypes, RestoreTypeFromAgentEnum } from 'wizards/models/type-data-models';
import { BackupFilter } from 'wizards/models/what-backup-tree-model';
import { WizardHelpLink, WizardHelpLinkHook } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardStepsManagerClass, WizardStepsManagerHook } from 'wizards/remote-management-wizards/strategy/wizard-steps-manager.class';
import {
  WizardStorageManagerClass,
  WizardStorageManagerHook
} from 'wizards/remote-management-wizards/strategy/wizard-storage-manager.class';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { WizardsServicesHubService } from 'wizards/services/wizards-services-hub.service';
import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { getBasePlanFromType } from 'wizards/utils/getBasePlanFromType';
import { WizardCombinedDataManagerClass } from './strategy/wizard-combined-data-manager.class';

@UntilDestroy()
@Directive()
export abstract class WizardBaseClass
  implements WizardHelpLinkHook, WizardStepsManagerHook, WizardStorageManagerHook, OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(WizardComponent, { static: true }) wizard: WizardComponent;

  public i18nPipe = inject(I18NextPipe);
  protected stepService = inject(WizardStepsService);
  private helpService = inject(HelpService);
  private services = inject(WizardsServicesHubService);
  protected cdr = inject(ChangeDetectorRef);
  protected injector = inject(Injector);

  public helpLink: WizardHelpLink<string> = new WizardHelpLink(this.helpService, this);
  public stepsManager = new WizardStepsManagerClass(this.i18nPipe, this.stepService, this);
  public storageManager = new WizardStorageManagerClass(this.stepService, this.stepsManager, this);
  public combinedDataManager = new WizardCombinedDataManagerClass(this);
  public FormModel: any;
  public isEdit = false;
  public isRDMode = false;
  public isGFSMode: boolean;
  public loadingPlan = false;
  public loadedPlan = null;
  public myPlan$: Subject<any> = new Subject();
  public orientations: 'horizontal' | 'vertical' = 'vertical';
  public planType: PlanTypes = 16;
  public planTypeText: string;
  public incrementalData: IncrementalData;
  public wizardFinish = false;
  public canBackupEmptyFolders = true;
  public invalidIntelligentRetentionStorage = false;
  public prependStepText = this.i18nPipe.transform('wizards:prepend_steps_if_offline');
  public mainService: RemoteManagementWizardsService;
  public rdService: RemoteDeployDataFacade;
  public agentOptionsService: AgentOptionsFacade;
  public backupFacade: ComputerBackupFacade;
  public ignorableSteps = [this.stepsManager.titles.advancedOptions];
  public ability: AbilityService;

  protected ABTest: ObservedValueOf<ReturnType<typeof this.stepService.pipeABTest>> = {};

  private modifiedPlanNotification: NotificationPerPlanSettings;

  public get agentSimpleUi(): boolean {
    return this.ability.can('read', 'AllowSimpleUi');
  }

  public get isNBF(): boolean {
    return this.mainService.isNBF;
  }

  public get isUnix(): boolean {
    return this.mainService.isUnix;
  }

  public get isNetBuild(): boolean {
    return this.mainService.isNetBuild;
  }

  public get isRestore(): boolean {
    return this.mainService.isRestore;
  }

  public get wizardTitle(): string {
    return `${this.isEdit ? 'Edit' : 'Create'} ${this.planTypeText || ''} ${this.mainService.compName ? 'for' : ''}`;
  }

  protected get needSubscribeThroughPipe(): boolean {
    return !this.isRDMode || (this.isRDMode && this.isEdit);
  }

  protected constructor() {
    this.ability = this.services.ability;
    this.mainService = this.services.mainService;
    this.rdService = this.services.rdService;
    this.agentOptionsService = this.services.agentOptionsService;
    this.backupFacade = this.services.backupFacade;

    this.setBaseData();

    if (this.isRDMode) {
      return void this.mainService.allowPrePostActions.next(true);
    }

    this.agentOptionsService
      .getComputerAgentOptions(this.mainService.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (opts) => this.mainService.allowPrePostActions.next(!!opts?.backupValues?.allowPrePostActions)
      });
  }

  ngOnInit(): void {
    this.loadingPlan = true;

    this.helpLink.initHelpDocumentationLinks();

    if (!this.isRDMode) {
      if (this.planType === PlanTypes.RestoreDiskImagePlan && this.mainService.targetComputerData) {
        this.loadAndSetTargetPlanData();
      }
      return void this.mainService.Plan$.pipe(
        switchMap((plan) =>
          plan && this.mainService.sourceHid && this.mainService.sessionId ? this.getStoragesForDiffComputer(plan) : of(plan)
        ),
        switchMap((plan) =>
          !plan ? this.backupFacade.getLegacyPlan(null, !this.mainService.hasAllRequiredDataToGetPlan).pipe(map(() => null)) : of(plan)
        ),
        untilDestroyed(this)
      ).subscribe((plan) => plan && this.setLoadedPlan(plan));
    }

    if (this.isEdit) return void this.loadDataIfRDAndEdit();

    this.setLoadedPlan();
  }

  getStoragesForDiffComputer(plan): Observable<unknown> {
    const hid: string = this.mainService.sourceHid;

    this.backupFacade.loadDestinations({ hid });

    const destinationsReq$ = toObservable(this.backupFacade.destinationsByHid(hid), { injector: this.injector });
    const destLoadingReq$ = toObservable(this.backupFacade.destinationsByHidLoading(hid), { injector: this.injector });

    return combineLatest([destinationsReq$, destLoadingReq$]).pipe(
      filter(([destinations, loading]) => !loading),
      first(),
      map(([destinations, loading]) => {
        const destinationItemsFullList = storageAccountsToDestinationsArray(destinations || []);
        const storages: Storages = { CloudStorages: [], LocalStorages: [] } as Storages;

        destinationItemsFullList.forEach((d) =>
          storages[d.StorageType === StorageType.LocalFS ? 'LocalStorages' : 'CloudStorages'].push(d)
        );

        plan.Storages = storages;

        return plan;
      })
    );
  }

  loadAndSetTargetPlanData(): void {
    this.mainService
      .GetRMPlanModel(
        {
          mode: this.mainService.mode.toString(),
          type: this.mainService.type.toString(),
          email: this.mainService.targetComputerData.email,
          planId: '',
          userId: this.mainService.targetComputerData.userId,
          hid: this.mainService.targetComputerData.hid,
          profile: this.mainService.profile || ''
        },
        true
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (plan) => {
          const newPlan = plan && (plan.plan || plan.Plan);
          const volumes = plan.DiskVolumes.map((x) => ({
            ...x,
            identityForUiField: `${x.Identity}__${x.DiskId}__${x.WindowsVolumeIdentity}`
          }));
          this.mainService.loadedDisksInfoForIbb.set({ volumes, disks: newPlan?.DiskInfo });
        }
      });
  }

  ngAfterViewInit() {
    this.stepsManager.registerVisitedStep();
  }

  stepSelectedHandler(step): void {
    this.stepsManager.registerVisitedStep(step, true);
  }

  nextHandler(step): void {
    this.stepsManager.registerVisitedStep(step, true);
  }

  defaultSettings(): WizardHelpersSettings {
    return {
      agentVersion: this.mainService.backupVersionUpdated,
      validVersionForSchedule: this.mainService.validVersionForSchedule,
      timeZoneOffset: this.mainService.timeZoneOffset,
      mode: this.mainService.mode,
      compName: this.mainService.compName,
      currentPrefix: this.mainService.currentPrefix,
      accounts: this.storageManager.getStorageAccounts(),
      isRD: this.isRDMode,
      isNBF: this.isNBF,
      agentSimpleUi: this.agentSimpleUi,
      isOffline: this.mainService.isOffline,
      initialFormData: this.mainService.initialFormData,
      planType: this.planType,
      isUnix: this.isUnix,
      isNetBuild: this.isNetBuild,
      allowPrePostActions: this.mainService.allowPrePostActions.value
    };
  }

  loadDataIfRDAndEdit(): void {
    this.storageManager.updateStorages();
    this.mainService.Plan$.pipe(untilDestroyed(this)).subscribe((plan) => {
      if (!plan) {
        const savedPlan = this.mainService.plansArray$.value.find((p) => p.plan.ID === this.mainService.planId);
        setTimeout(() => this.mainService.Plan$.next(savedPlan), 100);
        return;
      }
      this.setLoadedPlan(plan);
    });
  }

  setBaseData(): void {
    this.stepService.reset();
    this.initABTests();

    this.stepsManager.setMainService(this.mainService);
    this.storageManager.setRDService(this.rdService);

    this.isRDMode = this.mainService.wizardMode === PlansWizardMode.forRD;
    this.isEdit = this.mainService.mode === PlanMode.edit;
    this.planType = this.mainService.type;
    this.planTypeText = PlanTypesDisplay[this.mainService.type] || PlanVMNewTypesDisplay[this.mainService.type];
    this.mainService.password.next('');
  }

  setLoadedPlan(plan = null): void {
    const mainPlanSettings = plan?.plan || plan?.Plan;

    if (
      mainPlanSettings?.MBSToken ||
      mainPlanSettings?.RefreshToken ||
      mainPlanSettings?.MBSTokenEncrypted ||
      mainPlanSettings?.RefreshTokenEncrypted
    ) {
      this.mainService.closeAndReset();
      return this.mainService.openErrorVersionModal(
        true,
        this.i18nPipe.transform('wizards:cantEditDiffComputerPlanText'),
        this.i18nPipe.transform('wizards:cantEditDiffComputerPlanTitle')
      );
    }

    if (this.needCheckVMWizard()) {
      if (+this.mainService.backupVersionUpdated.substring(0, 3) < 761) {
        this.mainService.closeAndReset();
        this.mainService.openErrorVersionModal();
        return;
      }

      if (+this.mainService.backupVersionUpdated.substring(0, 3) < 783 && this.existExcludesIntoVMwarePlan(plan?.plan)) {
        this.mainService.closeAndReset();
        this.mainService.openErrorVersionModal(true, this.i18nPipe.transform('wizards:virtual_wizards_error_fields_exclude_text_alert'));
        return;
      }

      if (
        this.planType === PlanTypes.BackupHyperV &&
        plan?.plan?.ClusterBackup &&
        +this.mainService.backupVersionUpdated.substring(0, 2) < 79
      ) {
        this.mainService.closeAndReset();
        this.mainService.openErrorVersionModal(true);
        return;
      }

      if (this.planType === PlanTypes.RestoreVMware) {
        const unsupported = [RestoreTypeFromAgentEnum.RestoreToAzure, RestoreTypeFromAgentEnum.RestoreToEC2].includes(
          plan?.DiskImageRestoreType
        );

        if (unsupported) {
          this.mainService.closeAndReset();
          this.mainService.openErrorVersionModal(true);
          return;
        }
      }
    }

    if (this.mainService.needShowErrorVersionModalForUnix(plan?.plan?.IsArchive)) {
      this.mainService.showErrorVersionModalForUnix();
      return;
    }

    this.setStoragesAndLoadedPlan(plan);
  }

  setStoragesAndLoadedPlan(plan): void {
    if (plan) {
      if (!plan.Storages && this.FormModel.Storages.CloudStorages && this.FormModel.Storages.LocalStorages) {
        plan.Storages = {} as Storages;
        plan.Storages.CloudStorages = this.FormModel.Storages.CloudStorages;
        plan.Storages.LocalStorages = this.FormModel.Storages.LocalStorages;
      }
      if (this.isEdit && plan.plan) this.mainService.isNBF = !!plan.plan.IsArchive;
      this.myPlan$.next(plan);
    }

    this.loadingPlan = false;
    this.isGFSMode = this.FormModel?.retentionPolicyStep?.GFSPolicySettings?.enabled;
    this.incrementalData = this.FormModel.scheduleStep?.ScheduleType === 'ffi' ? this.FormModel.scheduleStep?.incrementalData : null;

    if (this.isRDMode) setTimeout(() => this.updateAvailablePlans(plan ? plan.plan.ID : ''));

    this.stepService
      .pipeProperty('schedule', 'forceDeactivateFFI')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.stepsManager.setCurrentStep(this.stepsManager.titles.schedule);

        this.mainService.openForceDeactivateFFIModal().then((result) => {
          if (result) {
            this.wizard.handleSave();
          }
        });
      });
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.mainService.planNotification = new NotificationPerPlanSettings();
      this.mainService.Plan$.next(null);
    });
  }

  // TODO: Move to RD manager
  updateAvailablePlans(id = ''): void {
    const keys = Object.keys(this.mainService.AvailablePlansForRD$.value[this.mainService.rdConfigurationId] || {});
    const AMPlans: { [key: string]: string } = {};
    keys.forEach((planId) => {
      AMPlans[this.mainService.AvailablePlansForRD$.value[this.mainService.rdConfigurationId][planId].plan.ID] =
        this.mainService.AvailablePlansForRD$.value[this.mainService.rdConfigurationId][planId].plan.Name;
    });
    this.FormModel.AvailableMachinePlans = StepsDataFromPlanHelper.updateAvailableMachinePlans(AMPlans, id);
    if (this.isRDMode) {
      this.FormModel.AvailableMachinePlans = this.FormModel.AvailableMachinePlans.filter((plan: DefaultSelectData) =>
        this.validateAvailablePlan(plan, id)
      );
    }
  }

  // TODO: Move to RD manager
  validateAvailablePlan(plan: DefaultSelectData, id: string, plans = this.mainService.plansArray$.value): boolean {
    const currentIdx: number = plans.findIndex((p) => p.plan.ID === plan.value);
    const foundPlan = ~currentIdx && plans[currentIdx].plan;

    if (~currentIdx && foundPlan.NextExectutionPlan === id) return false;

    return foundPlan && foundPlan.NextExectutionPlan && foundPlan.NextExectutionPlan !== GuidEmpty
      ? this.validateAvailablePlan(
          { value: foundPlan.NextExectutionPlan, label: foundPlan.Name },
          id,
          plans.filter((_, idx) => idx !== currentIdx)
        )
      : true;
  }

  setLoadedPlanAndRunChainRequestsForBackup(plan): void {
    this.loadedPlan = plan;
    if (this.isEdit && !this.isRDMode) this.mainService.GetPlanNotificationOptions();
    else if (!this.isRDMode) this.mainService.getGlobalNotification();
  }

  setDefaultValue(): void {
    this.stepsManager.setDefaultPlanName();
    this.loadedPlan.plan = getBasePlanFromType(this.planType);

    if (this.planType === PlanTypes.Plan) this.loadedPlan.plan.BackupFilter = new BackupFilter();

    this.storageManager.updateStorages();
  }

  restoreOnceHandler(event): void {
    this.stepsManager.setScheduleRestoreOnce(!!event);
  }

  notificationChangeHandler(event: NotificationPerPlanSettings): void {
    this.modifiedPlanNotification = event;
  }

  hybridChangedToDefaultHandler(event): void {
    this.stepsManager.setHybridToDefault(!!event);
  }

  scheduleTypeChangeHandler(event: string): void {
    this.stepService.updateProperty('schedule', 'isFFI', event === 'ffi');

    if ((!this.isNBF || this.isRestore) && this.loadedPlan) {
      const plan = this.loadedPlan.plan || this.loadedPlan.Plan;

      if (event === 'recurring') this.updateAdvancedSchedule(plan);
      if (event === 'predefined') this.updateSimpleScheduleStep(plan);
    }
  }

  updateAdvancedSchedule(plan): void {
    this.stepsManager.updateAdvancedSchedule(plan, this.loadedPlan);
  }

  updateSimpleScheduleStep(plan): void {
    this.stepsManager.updateSimpleScheduleStep(plan, this.loadedPlan);
  }

  setIncrementalData(data: IncrementalData): void {
    this.incrementalData = data;
  }

  forceBackStepWizard(name: string, title: string): void {
    this.stepsManager.forceBackStepWizard(name, title);
  }

  forceNextStepWizard(isSave: boolean, name: string, title: string): void {
    this.stepsManager.forceNextStepWizard(isSave, name, title);
  }

  forceValidStep(name: string): void {
    this.FormModel[name].valid = true;
  }

  generatePlanForRDFormat(plan): RemoteDeployPlanModel {
    const newPlan = {
      AvailableFeatures: {},
      AvailableMachinePlans: null,
      DiskVolumes: null,
      IsUnix: this.isUnix,
      OverdueAfterSuccess: null,
      plan,
      PlanType: this.planType,
      Storages: null
    } as RemoteDeployPlanModel;

    this.mainService.updatePlanTextElements(newPlan, this.rdService.allStorageAccounts);
    this.mainService.updatePlanTextRetention(newPlan);
    this.mainService.setValidForRDStatuses(newPlan);

    return newPlan;
  }

  savePlan(newPlan): void {
    if (this.wizardFinish) {
      return;
    }

    if (!this.isEdit) newPlan.IsArchive = this.isNBF;

    if (this.isRDMode) {
      this.storageManager.updateBucket(newPlan);

      const storageAccount = this.storageManager.getStorageAccountByBucketId(newPlan.ConnectionID);

      if (storageAccount) newPlan.SGAccountID = storageAccount.id;

      this.mainService.updateArrayOfPlans(!this.isEdit ? this.generatePlanForRDFormat(newPlan) : newPlan);
      this.wizardFinish = true;

      return;
    }

    this.saveIsNotRD(newPlan);
  }

  saveIsNotRD(plan): void {
    const final = () => {
      this.backupFacade
        .saveLegacyPlan(plan)
        .pipe(catchError(() => of(false)))
        .subscribe((result) => this.updatesAfterSave(result, plan));
    };

    this.loadingPlan = true;

    this.getPlanStatus()
      .pipe(take(1))
      .subscribe((status) => {
        if (status !== SimplePlanStatus.Running) {
          this.loadingPlan = false;

          return void final();
        }

        this.mainService.openErrorRunningPlan().subscribe({
          next: (answer: SaveVariants) => {
            this.loadingPlan = false;

            if (answer === SaveVariants.notSave) return;

            if (answer === SaveVariants.stopAndSave) this.stopPlan().subscribe(() => final());
            else final();
          },
          error: () => {
            this.wizard.spinnerShow = false;
            this.loadingPlan = false;
          }
        });
      });
  }

  updatesAfterSave(result, newPlan): void {
    if (result && CloudRestoreTypes.includes(this?.FormModel?.typeDataStep?.restoreAs)) return this.mainService.navigateToCloud();

    if (result && this.planType !== PlanTypes.CloudVMBackupEC2Plan) {
      const notification: NotificationPerPlanSettings = {
        ...(this.modifiedPlanNotification ? this.modifiedPlanNotification : this.mainService.planNotification),
        userId: this.mainService.targetComputerData ? this.mainService.targetComputerData.userId : this.mainService.userId,
        planId: newPlan.ID
      };

      this.stepsManager.updateDestinationAsDefault();
      this.stepsManager.updateOverdueAlert(newPlan, notification, this.mainService.targetComputerData?.hid);
    }

    if (!result) {
      this.wizard.spinnerShow = false;
      this.loadingPlan = false;

      return;
    }

    this.wizardFinish = true;
  }

  loadInfoHandler(event: StepLoadInfo): void {
    this.loadingPlan = this.stepsManager.processLoadingStep(event.loading);

    queueMicrotask(() => this.loadInfoHandlerFinally(event));
  }

  protected abstract loadInfoHandlerFinally(event?: StepLoadInfo): void;

  protected abstract getPlanForSaving(): any;

  handleModalClose(): void {
    this.cdr.detach();
  }

  handleSave(): void {
    this.savePlan(this.getPlanForSaving());
  }

  handleCopyRAWPlan(): void {
    const plan = this.getPlanForSaving();

    window.navigator.clipboard.writeText(JSON.stringify(plan, null, 2));
  }

  protected initSubscribes(): void {
    if (this.needSubscribeThroughPipe) {
      this.myPlan$
        .pipe(
          first((p) => !!p),
          untilDestroyed(this)
        )
        .subscribe((plan) => plan && this.updateStepData(plan));

      return;
    }

    this.updateStepData(new PlanResponseModel(), true);
  }

  protected abstract updateStepData(plan, isDefault?: boolean): void;

  private getPlanStatus(): Observable<SimplePlanStatus> {
    if (!this.isEdit) return of(SimplePlanStatus.Unknown);

    return this.backupFacade
      .getComputerPlans(this.mainService.hid)
      .pipe(map((result) => result.planInfos.find((plan) => plan.id === this.mainService.planId)?.status));
  }

  private stopPlan(): Observable<boolean> {
    return this.backupFacade.stopPlan({ hid: this.mainService.hid }, this.mainService.planId);
  }

  private needCheckVMWizard(): boolean {
    return this.isEdit && (this.planType === PlanTypes.BackupVMware || this.planType === PlanTypes.BackupHyperV);
  }

  private existExcludesIntoVMwarePlan(plan): boolean {
    return plan?.VirtualMachinesEx?.some((m) => m.DiskInfo?.some((di) => di.Volumes?.some((v) => v.BackupOptions?.ExcludeRules?.length)));
  }

  overrideNeedShowHelpLink(): boolean {
    return (this.isNBF || this.isRestore || this.helpLink.helpLinkTypes.includes(this.planType)) && this.helpLink.canShowHelpLink;
  }

  private initABTests(): void {
    this.stepService
      .pipeProperty('ABTest')
      .pipe(untilDestroyed(this))
      .subscribe((data) => (this.ABTest = data));

    this.stepService.updateProperty('ABTest', 'DestinationPlusRetentionStep', this.isNBF && !this.isRDMode);
  }
}
